// @file For frame previews on tldraw canvas
// @file For whiteboard frame previews -- used in frame bar and linking panel

import { getEditorObject } from '@@/bits/tldraw/editor'
import { unboundedWatch } from '@@/bits/vue'
import {
  BackgroundType,
  BACKGROUND_TYPE_TO_URL_MAPPING,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  isShapeApproved,
} from '@@/bits/whiteboard'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { debounce, flattenDeep, uniq } from 'lodash-es'
import { defineStore } from 'pinia'
import { Box, type HistoryEntry, type TLPageId, type TLRecord, type TLShapeId } from 'tldraw'
import { ref } from 'vue'

// TODO: Replace with url to 1px blank image to increase performance
export const BLANK_FRAME_BASE64 = 'data:image/gif;base64,R0lGODlhAQABAIAAAP7//wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw=='

async function getSvgStringFromShapeIds(shapeIds: TLShapeId[]): Promise<string> {
  const editor = getEditorObject()
  if (editor == null) {
    return `<div></div>`
  }
  if (shapeIds.length === 0) {
    return `<div></div>`
  }
  const approvedShapeIds = shapeIds.filter((shapeId) => isShapeApproved(editor.getShape(shapeId)))
  const svgData = await editor.getSvgString(approvedShapeIds, {
    scale: 1,
    background: false,
    bounds: new Box(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT),
    preserveAspectRatio: 'xMidYMin slice',
    padding: -4,
  })
  if (svgData == null) {
    return `<div></div>`
  }

  return svgData.svg
}

async function getSvgStringFromFrameBackground(pageId: TLPageId): Promise<string> {
  const editor = getEditorObject()
  if (editor == null) {
    return `<div></div>`
  }
  const page = editor.getPage(pageId)
  if (page == null) {
    return `<div></div>`
  }
  const meta = page.meta
  const backgroundType = meta.backgroundType as BackgroundType
  let backgroundImageUrl = ''
  let backgroundStyleString = ''
  if (backgroundType === BackgroundType.Custom) {
    backgroundImageUrl = (meta.customBackgroundUrl as string) ?? BACKGROUND_TYPE_TO_URL_MAPPING[BackgroundType.Plain]
    backgroundStyleString = `background-repeat: no-repeat; background-size: cover;`
  } else {
    backgroundImageUrl = BACKGROUND_TYPE_TO_URL_MAPPING[backgroundType ?? BackgroundType.Plain]
    backgroundStyleString = `background-repeat: repeat; background-size: contain;`
  }
  return `<div style="position: absolute; inset: 0; z-index: -1; background-image: url(${backgroundImageUrl}); ${backgroundStyleString}"></div>`
}

export const useWhiteboardFramePreviewStore = defineStore('whiteboardFramePreview', () => {
  const whiteboardStore = useWhiteboardStore()
  /**
   * FRAME LIVE PREVIEW
   */

  const framePreviews = ref<Record<string, string>>({})
  const framesWithPreviewChanges = ref<string[]>([])

  // Helpers
  async function updatePreviewForSingleFrame(specificPageId: TLPageId): Promise<void> {
    const editor = getEditorObject()
    if (editor == null) return
    const shapeIds = Array.from(editor.getPageShapeIds(specificPageId))
    // const img = await getImageUrlFromShapeIds(shapeIds)
    const svg = await getSvgStringFromShapeIds(shapeIds)
    const frameBgSvg = await getSvgStringFromFrameBackground(specificPageId)
    const frame = whiteboardStore.frames.find((frame) => frame.id === specificPageId)
    if (frame != null) {
      framePreviews.value = {
        ...framePreviews.value,
        [specificPageId]: `${frameBgSvg}\n${svg}`,
      }
    }
  }

  async function updatePreviewForAllFrames(specificPageId?: TLPageId): Promise<void> {
    const editor = getEditorObject()
    if (editor == null) return
    const pages = editor.getPages()
    if (specificPageId != null) {
      void updatePreviewForSingleFrame(specificPageId)
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    pages.forEach(async (page) => {
      await updatePreviewForSingleFrame(page.id)
    })
  }

  // Listen to frame content changes and track which frames have changes
  function handlePageChange(event: HistoryEntry<TLRecord>): void {
    const pagesWithChange = uniq(
      flattenDeep([
        Object.values(event.changes.added),
        Object.values(event.changes.updated),
        Object.values(event.changes.removed),
      ])
        .map((change) => {
          if (change.typeName === 'shape') {
            return change.parentId
          }
          if (change.typeName === 'page') {
            return change.id
          }
          return ''
        })
        .filter((pageId) => pageId != null),
    )
    if (pagesWithChange.length === 0) return
    framesWithPreviewChanges.value = uniq([...framesWithPreviewChanges.value, ...pagesWithChange])
  }

  // Debounce the handlePageChange so that it only runs once every 1s
  // maxWait is set to 1000 so that it will run at most once every 1s when there are multiple changes
  const debouncedHandlePageChange = debounce(handlePageChange, 1000, { maxWait: 1000, trailing: true })

  // When there is a frame with preview changes, run the actual logic to update the preview for the frames

  function conditionallyUpdateFramesWithChanges(): void {
    if (framesWithPreviewChanges.value.length === 0) return
    const editor = getEditorObject()
    // Only update the preview if the editor is in an idle state, otherwise on shitty devices, it will lag
    if (
      editor == null ||
      !editor.isInAny(
        'draw.idle',
        'select.idle',
        'highlight.idle',
        'text.idle',
        'padlet-draw-eraser.idle',
        'laser.idle',
        'note.idle',
        'geo.idle',
        'interact',
      )
    ) {
      return
    }
    framesWithPreviewChanges.value.forEach((pageId) => {
      void updatePreviewForSingleFrame(pageId as TLPageId)
    })
    framesWithPreviewChanges.value = []
  }

  void unboundedWatch(framesWithPreviewChanges, conditionallyUpdateFramesWithChanges)

  // When there is a new frame, add a blank frame preview
  void unboundedWatch(
    () => whiteboardStore.frames,
    (newFrameList, oldFrameList) => {
      const addedFrames = newFrameList.filter(
        (frame) => oldFrameList.find((oldFrame) => oldFrame.id === frame.id) == null,
      )
      if (addedFrames.length > 0) {
        framePreviews[addedFrames[0].id] = BLANK_FRAME_BASE64
      }
    },
  )

  return {
    // State
    framePreviews,
    framesWithPreviewChanges,
    conditionallyUpdateFramesWithChanges,

    // Actions
    updatePreviewForSingleFrame,
    updatePreviewForAllFrames,
    debouncedHandlePageChange,
  }
})
